/**
 * personalization-cart-attributes.js
 * Viori Personalizer — Cart attribute persistence (spec §9d fallback)
 *
 * Purpose:
 *   Write VioriState to Shopify cart attributes at add-to-cart time.
 *   This acts as a fallback for the thank-you page metafield write:
 *   if the thank-you script fails (popup blockers, aggressive extensions),
 *   the checkout extension reads the cart attribute and fires the worker call.
 *
 * What gets written:
 *   attributes["viori_hairType"]       — e.g. "oily"
 *   attributes["viori_hairGoals"]      — e.g. "hairGrowth,improveShineSoftness"
 *   attributes["viori_hairConcerns"]   — e.g. "thinning,dandruff"
 *   attributes["viori_hairLookALike"]  — e.g. "brunette"
 *   attributes["viori_whoIsThisFor"]   — e.g. "myself"
 *   attributes["viori_otherReasons"]   — e.g. "clean,sustainable"
 *   attributes["viori_quizComplete"]   — "true" | "false"
 *   attributes["viori_utmSource"]      — e.g. "facebook"
 *   attributes["viori_utmMedium"]      — e.g. "cpc"
 *   attributes["viori_utmCampaign"]    — e.g. "spring25"
 *   attributes["viori_sessionId"]      — UUID from localStorage viori_sid
 *
 * Cart attributes are plain text fields (max 255 chars each).
 * Arrays are comma-joined since cart attributes don't support JSON arrays.
 *
 * Integration:
 *   Include this file in the theme via:
 *     {{ 'personalization-cart-attributes.js' | asset_url | script_tag }}
 *   OR (preferred) load it in the product template via:
 *     <script src="{{ 'personalization-cart-attributes.js' | asset_url }}" defer></script>
 *
 * Spec refs: Section 9d
 * Dependencies: personalization-state.js (VioriState must be initialized first)
 */

(function () {
  'use strict';

  const CART_API     = '/cart/update.js';
  const DEBOUNCE_MS  = 800;  // avoid hammering cart API on rapid state changes
  let   _debounceTimer;
  let   _lastWritten = '';   // avoid redundant writes

  // ── Build cart attribute payload from VioriState ─────────────────────────
  function _buildAttributes(state) {
    if (!state) return {};

    function _join(val) {
      if (!val) return '';
      return Array.isArray(val) ? val.join(',') : String(val);
    }

    return {
      viori_hairType:      _join(state.hairType)      || '',
      viori_hairGoals:     _join(state.hairGoals)     || '',
      viori_hairConcerns:  _join(state.hairConcerns)  || '',
      viori_hairLookALike: _join(state.hairLookALike) || '',
      viori_whoIsThisFor:  _join(state.whoIsThisFor)  || '',
      viori_otherReasons:  _join(state.otherReasons)  || '',
      viori_quizComplete:  state.quizComplete ? 'true' : 'false',
      viori_utmSource:     _join(state.utmSource)     || '',
      viori_utmMedium:     _join(state.utmMedium)     || '',
      viori_utmCampaign:   _join(state.utmCampaign)   || '',
      viori_sessionId:     _vioriSessionId()           || '',
    };
  }

  // ── Get session ID from localStorage ────────────────────────────────────
  function _vioriSessionId() {
    try { return localStorage.getItem('viori_sid') || ''; } catch (_) { return ''; }
  }

  // ── Write attributes to Shopify cart via AJAX API ─────────────────────
  function _writeToCart(attributes) {
    const fingerprint = JSON.stringify(attributes);
    if (fingerprint === _lastWritten) return; // nothing changed
    _lastWritten = fingerprint;

    // Strip empty values to keep cart clean
    const clean = {};
    Object.keys(attributes).forEach(k => {
      if (attributes[k]) clean[k] = attributes[k];
    });

    fetch(CART_API, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ attributes: clean }),
    })
    .then(r => {
      if (!r.ok) console.warn('[Viori] Cart attribute write failed:', r.status);
    })
    .catch(e => console.warn('[Viori] Cart attribute write error:', e));
  }

  // ── Debounced writer (avoids thrashing on rapid state changes) ───────────
  function _scheduleWrite() {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => {
      const state = _getVioriState();
      if (!state) return; // no state, nothing to write
      const attrs = _buildAttributes(state);
      _writeToCart(attrs);
    }, DEBOUNCE_MS);
  }

  // ── Get current VioriState from global or localStorage ───────────────────
  function _getVioriState() {
    // Prefer live VioriState object (set by personalization-state.js)
    if (window.VioriState && typeof window.VioriState.get === 'function') {
      return window.VioriState.get();
    }
    // Fallback: read directly from localStorage
    try {
      const raw = localStorage.getItem('viori_state');
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }

  // ── Write on every state change ───────────────────────────────────────────
  document.addEventListener('viori:state_change', _scheduleWrite);

  // ── Also intercept add-to-cart clicks to ensure attributes are fresh ─────
  // Catches native ATC forms and AJAX ATC buttons
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || form.action.indexOf('/cart/add') === -1) return;
    // Synchronous write on ATC — skip debounce
    const state = _getVioriState();
    if (!state) return;
    const attrs = _buildAttributes(state);
    const fingerprint = JSON.stringify(attrs);
    if (fingerprint === _lastWritten) return;
    _lastWritten = fingerprint;

    const clean = {};
    Object.keys(attrs).forEach(k => { if (attrs[k]) clean[k] = attrs[k]; });

    // Best-effort fire-and-forget — we don't block form submission
    navigator.sendBeacon
      ? navigator.sendBeacon(CART_API, new Blob(
          [JSON.stringify({ attributes: clean })],
          { type: 'application/json' }
        ))
      : fetch(CART_API, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ attributes: clean }),
          keepalive: true,
        }).catch(() => {});
  }, true /* capture phase — fires before default */);

  // ── Initial write (page load) ────────────────────────────────────────────
  // Wait briefly for VioriState to initialize (personalization-state.js)
  setTimeout(() => {
    const state = _getVioriState();
    if (!state) return;
    const attrs = _buildAttributes(state);
    // Only write if there's meaningful state (at least hairType or quizComplete)
    if (!attrs.viori_hairType && attrs.viori_quizComplete !== 'true') return;
    _writeToCart(attrs);
  }, 1200);

  // ── Verification helper (dev/testing) ────────────────────────────────────
  // In browser console: window.VioriCartVerify() → logs current cart attributes
  window.VioriCartVerify = async function () {
    const resp  = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart  = await resp.json();
    const attrs = cart.attributes || {};
    const vioriKeys = Object.keys(attrs).filter(k => k.startsWith('viori_'));
    if (!vioriKeys.length) {
      console.log('[Viori] Cart attributes: none found (state may not be set yet)');
      return;
    }
    console.group('[Viori] Cart attributes verification');
    vioriKeys.forEach(k => console.log(`  ${k}: ${attrs[k]}`));
    console.groupEnd();
    return attrs;
  };

})();
